import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CheckCircle,
  XCircle,
  Calendar,
  User,
  Clock,
  MoreVertical,
  Trash2,
  Edit,
  Loader2,
} from 'lucide-react';
import { useTaskStore } from '../../store/taskStore';
import type { Task, TaskStatus } from '../../types';
import { effectiveFirstName, effectiveLastName } from '../../lib/personPrecedence';
import toast from 'react-hot-toast';

interface TaskCardProps {
  task: Task;
  showPerson?: boolean; // Whether to show linked person
  onEdit?: (task: Task) => void;
}

// Status badge styles
const STATUS_STYLES: Record<TaskStatus, string> = {
  PENDING: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-400',
  COMPLETED: 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400',
  CANCELLED: 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400',
};

// Format due date with relative time
function formatDueDate(dateString: string): { text: string; isOverdue: boolean; isToday: boolean } {
  const dueDate = new Date(dateString);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const dueDateOnly = new Date(dueDate.getFullYear(), dueDate.getMonth(), dueDate.getDate());

  const diffDays = Math.floor((dueDateOnly.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

  if (diffDays < 0) {
    const overdueDays = Math.abs(diffDays);
    return {
      text: overdueDays === 1 ? 'Yesterday' : `${overdueDays} days overdue`,
      isOverdue: true,
      isToday: false,
    };
  }

  if (diffDays === 0) {
    return { text: 'Today', isOverdue: false, isToday: true };
  }

  if (diffDays === 1) {
    return { text: 'Tomorrow', isOverdue: false, isToday: false };
  }

  if (diffDays < 7) {
    return { text: `In ${diffDays} days`, isOverdue: false, isToday: false };
  }

  // Show actual date for later tasks
  return {
    text: dueDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    isOverdue: false,
    isToday: false,
  };
}

/**
 * Single task card with quick actions
 */
export const TaskCard = ({ task, showPerson = true, onEdit }: TaskCardProps) => {
  const navigate = useNavigate();
  const { completeTask, cancelTask, deleteTask, isSaving } = useTaskStore();
  const [showMenu, setShowMenu] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const dueInfo = formatDueDate(task.dueDate);
  const isPending = task.status === 'PENDING';
  // Coach-change heads-up tasks get a dedicated face: chip, unclamped evidence,
  // and explicit Review/Dismiss actions (dismiss = CANCELLED; the real flag
  // raise auto-COMPLETEs the task server-side).
  const isCoachMove = task.meta?.kind === 'COACH_MOVE_NOTIFY';

  // Handle complete
  const handleComplete = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isPending || isProcessing) return;

    setIsProcessing(true);
    try {
      await completeTask(task.id);
      toast.success('Task completed!');
    } catch (error) {
      // Error handled in store
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle cancel
  const handleCancel = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isPending || isProcessing) return;

    setIsProcessing(true);
    try {
      await cancelTask(task.id);
      toast.success(isCoachMove ? 'Report dismissed' : 'Task cancelled');
    } catch (error) {
      // Error handled in store
    } finally {
      setIsProcessing(false);
    }
  };

  // Coach-move: jump to the coach with the deep link that auto-opens the
  // prefilled "Coach no longer here" sheet.
  const handleReviewChange = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (task.person) {
      navigate(`/people/${task.person.id}?coachFlag=1`);
    }
  };

  // Handle delete
  const handleDelete = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowMenu(false);

    if (!confirm('Are you sure you want to delete this task?')) {
      return;
    }

    setIsProcessing(true);
    try {
      await deleteTask(task.id);
      toast.success('Task deleted');
    } catch (error) {
      // Error handled in store
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle edit
  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowMenu(false);
    onEdit?.(task);
  };

  // Navigate to linked person
  const handlePersonClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (task.person) {
      navigate(`/people/${task.person.id}`);
    }
  };

  return (
    <div
      className={`bg-white dark:bg-gray-800 border rounded-lg p-4 hover:shadow-sm transition-shadow ${
        isCoachMove && isPending
          ? 'border-amber-300 dark:border-amber-700'
          : dueInfo.isOverdue && isPending
            ? 'border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20'
            : 'border-gray-200 dark:border-gray-700'
      } ${task.status !== 'PENDING' ? 'opacity-60' : ''}`}
    >
      <div className="flex items-start gap-3">
        {/* Complete/Status indicator */}
        <div className="flex-shrink-0 pt-0.5">
          {isPending ? (
            <button
              onClick={handleComplete}
              disabled={isProcessing}
              className="w-6 h-6 rounded-full border-2 border-gray-300 dark:border-gray-600 hover:border-green-500 hover:bg-green-50 dark:hover:bg-green-900/30 flex items-center justify-center transition-colors disabled:opacity-50"
              title="Mark as complete"
            >
              {isProcessing ? (
                <Loader2 className="w-4 h-4 text-gray-400 animate-spin" />
              ) : (
                <CheckCircle className="w-4 h-4 text-gray-300 dark:text-gray-600 hover:text-green-500" />
              )}
            </button>
          ) : task.status === 'COMPLETED' ? (
            <CheckCircle className="w-6 h-6 text-green-500" />
          ) : (
            <XCircle className="w-6 h-6 text-gray-400" />
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {isCoachMove && (
            <span className="inline-flex items-center px-2 py-0.5 mb-1 rounded-full text-[0.7rem] font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
              Coach change report
            </span>
          )}
          {/* Title */}
          <h4
            className={`text-sm font-medium ${
              task.status !== 'PENDING' ? 'line-through text-gray-500 dark:text-gray-400' : 'text-gray-900 dark:text-gray-100'
            }`}
          >
            {task.title}
          </h4>

          {/* Description (if exists) — coach-move evidence renders unclamped */}
          {task.description && (
            <p
              className={`text-sm text-gray-500 dark:text-gray-400 mt-1 ${
                isCoachMove ? 'whitespace-pre-line' : 'line-clamp-2'
              }`}
            >
              {task.description}
            </p>
          )}

          {/* Meta info */}
          <div className="flex flex-wrap items-center gap-3 mt-2 text-xs text-gray-500 dark:text-gray-400">
            {/* Due date */}
            <span
              className={`flex items-center gap-1 ${
                dueInfo.isOverdue && isPending
                  ? 'text-red-600 dark:text-red-400 font-medium'
                  : dueInfo.isToday && isPending
                  ? 'text-orange-600 dark:text-orange-400 font-medium'
                  : ''
              }`}
            >
              <Calendar className="w-3.5 h-3.5" />
              {dueInfo.text}
            </span>

            {/* Linked person */}
            {showPerson && task.person && (
              <button
                onClick={handlePersonClick}
                className="flex items-center gap-1 hover:text-blast-600 transition-colors"
              >
                <User className="w-3.5 h-3.5" />
                {effectiveFirstName(task.person)} {effectiveLastName(task.person)}
              </button>
            )}

            {/* Completed timestamp */}
            {task.completedAt && (
              <span className="flex items-center gap-1">
                <Clock className="w-3.5 h-3.5" />
                Completed {new Date(task.completedAt).toLocaleDateString()}
              </span>
            )}
          </div>

          {/* Coach-move explicit actions */}
          {isCoachMove && isPending && (
            <div className="flex flex-col sm:flex-row gap-2 mt-3">
              <button
                type="button"
                onClick={handleReviewChange}
                disabled={isProcessing || !task.person}
                className="btn-primary justify-center text-sm py-1.5 disabled:opacity-50"
              >
                Review &amp; make the change
              </button>
              <button
                type="button"
                onClick={handleCancel}
                disabled={isProcessing}
                className="btn-secondary justify-center text-sm py-1.5 disabled:opacity-50"
              >
                Coach is still here — dismiss
              </button>
            </div>
          )}
        </div>

        {/* Actions menu */}
        {isPending && (
          <div className="relative">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowMenu(!showMenu);
              }}
              className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded transition-colors"
            >
              <MoreVertical className="w-4 h-4" />
            </button>

            {showMenu && (
              <>
                {/* Backdrop */}
                <div
                  className="fixed inset-0 z-10"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowMenu(false);
                  }}
                />

                {/* Menu */}
                <div className="absolute right-0 top-8 z-20 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg py-1 min-w-[140px]">
                  {onEdit && (
                    <button
                      onClick={handleEdit}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                    >
                      <Edit className="w-4 h-4" />
                      Edit
                    </button>
                  )}
                  <button
                    onClick={handleCancel}
                    disabled={isProcessing}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
                  >
                    <XCircle className="w-4 h-4" />
                    Cancel
                  </button>
                  <button
                    onClick={handleDelete}
                    disabled={isProcessing}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 disabled:opacity-50"
                  >
                    <Trash2 className="w-4 h-4" />
                    Delete
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
